import { GET_API, POST_API } from "../../Functionalities/APIs";
import { csvLine } from "./csv";
import { basisParam, DAY_COMPLETED } from "../../Functionalities/dayBasis";

export const SLABS_KEY = "incentiveSlabs";
const RIDER_SETTLEMENT_URL = "/accounts/settlement/riders/";
const RIDER_SETTLEMENT_CLEAR_URL = "/accounts/settlement/riders/clear/";
const RIDER_SALARY_URL = "/accounts/settlement/riders/salary/";

const bodyOf = (result) => (result && result.data ? result.data : null);
const num = (value) => Number(value) || 0;

export const loadSlabs = (storage) => {
  try {
    const parsed = JSON.parse(storage.getItem(SLABS_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveSlabs = (storage, slabs) => storage.setItem(SLABS_KEY, JSON.stringify(slabs));

const slabOf = ({ ordersUpto, amount }) => ({ ordersUpto: Number(ordersUpto), amount: Number(amount) });
const bySize = (a, b) => a.ordersUpto - b.ordersUpto;

export const addSlab = (slabs, slab) => [...slabs, slabOf(slab)].sort(bySize);

export const updateSlab = (slabs, index, slab) =>
  slabs.map((existing, at) => (at === index ? slabOf(slab) : existing)).sort(bySize);

export const removeSlab = (slabs, index) => slabs.filter((_, at) => at !== index);

// The first slab the rider's order count fits under pays that much per order.
export const incentiveFor = (orderCount, slabs) => {
  const slab = (slabs || []).find((candidate) => num(orderCount) <= num(candidate.ordersUpto));
  return slab ? num(orderCount) * num(slab.amount) : 0;
};

// What the admin may type per rider; blank means "as the backend or the slabs say".
export const entryFor = (rider, slabs) => {
  const statement = rider.statement;
  return {
    incentive: statement ? String(statement.incentive) : String(incentiveFor(rider.orders || 0, slabs)),
    special: statement ? String(statement.special_incentive) : "",
    correction: statement ? String(statement.correction) : "",
    notes: statement ? statement.notes || "" : "",
  };
};

// Salary for the day is the day's delivery charges, surge and tips plus the incentive
// and special incentive, less the correction; "to admin" is the day's cash
// collected beyond that salary. Cash in hand and charges owed are the running
// ledger balances, which "Clear delivery collection" books.
export const settlementOf = (rider, entry) => {
  const day = rider.day || {};
  const deliveryCharges = num(day.delivery_charges);
  const tips = num(day.tips);
  const cash = num(day.cash);
  const incentive = num(entry.incentive);
  const special = num(entry.special);
  const correction = num(entry.correction);
  const surge = num(rider.surge);
  const salary = deliveryCharges + surge + tips + incentive + special - correction;
  return {
    deliveryCharges,
    tips,
    cash,
    incentive,
    special,
    correction,
    salary,
    toAdmin: cash - salary,
    surge,
    ongoing: num(rider.ongoing_orders),
    cashInHand: num(rider.collection),
    chargesOwed: num(rider.earnings),
  };
};

const inr = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const rupee = (value) => `₹${inr.format(Math.round(num(value)))}`;

export const statementLine = (statement) =>
  statement
    ? [
        `Salary ${rupee(statement.salary)}`,
        `taken from cash ${rupee(statement.salary_from_cash)}`,
        `cash in hand now ${rupee(statement.cash_in_hand_after)}`,
        `still owed to rider ${rupee(statement.charges_owed_after)}`,
      ].join(" · ")
    : "";

export const riderCsv = (riders, entries, slabs) => {
  const header = [
    "ID", "Name", "Ongoing", "Orders", "Delivery charges", "Tips", "Surge", "Incentive", "Special incentive",
    "Correction", "Salary", "Cash collected", "To admin", "Cash in hand", "Charges owed", "Salary from cash",
    "Cash in hand after", "Charges owed after", "Notes", "Submitted",
  ];
  const lines = riders.map((rider) => {
    const entry = entries[rider.id] || entryFor(rider, slabs);
    const s = settlementOf(rider, entry);
    const statement = rider.statement || {};
    return csvLine([
      Number(rider.id), rider.name || "", s.ongoing, rider.orders || 0, s.deliveryCharges, s.tips, s.surge, s.incentive,
      s.special, s.correction, s.salary, s.cash, s.toAdmin, s.cashInHand, s.chargesOwed,
      num(statement.salary_from_cash), num(statement.cash_in_hand_after), num(statement.charges_owed_after),
      entry.notes || "", rider.statement ? "yes" : "no",
    ]);
  });
  return [header.join(","), ...lines].join("\n");
};

// One request writes or rewrites the rider's statement for the day; the backend
// books the incentive part into the rider's ledger and pushes the rider.
export const submitRiderSalary = async (date, riderId, entry) => {
  const data = new FormData();
  data.append("rider_id", riderId);
  data.append("date", date);
  data.append("incentive", entry.incentive || "0");
  data.append("special_incentive", entry.special || "0");
  data.append("correction", entry.correction || "0");
  data.append("notes", entry.notes || "");
  return bodyOf(await POST_API(data, "", RIDER_SALARY_URL));
};

// One request: the day's orders per rider, their balances and ongoing orders,
// added up by the backend (accounts/settlement.py).
// Submitting books the salary out of the rider's cash, so the row and the page totals take the new balances at once.
export const withSubmittedStatement = (sheet, riderId, statement) => {
  const rider = sheet.riders.find((r) => r.id === riderId);
  if (!rider) return sheet;
  const collection = num(statement.cash_in_hand_after);
  const earnings = num(statement.charges_owed_after);
  return {
    ...sheet,
    riders: sheet.riders.map((r) => (r.id === riderId ? { ...r, statement, collection, earnings } : r)),
    totals: {
      ...sheet.totals,
      collection: num(sheet.totals.collection) - num(rider.collection) + collection,
      earnings: num(sheet.totals.earnings) - num(rider.earnings) + earnings,
    },
  };
};

export const fetchRiderSettlement = async (date) =>
  bodyOf(await GET_API("", `date=${date}${basisParam(DAY_COMPLETED)}`, RIDER_SETTLEMENT_URL));

// One request books the cash received from and the delivery charges paid to
// every listed rider; the backend refuses after 10 AM India.
export const clearRiderCollections = async (date, riderIds) => {
  const data = new FormData();
  data.append("date", date);
  data.append("rider_ids", JSON.stringify(riderIds));
  return bodyOf(await POST_API(data, "", RIDER_SETTLEMENT_CLEAR_URL));
};
